import React, { useState, useEffect, useLayoutEffect, Suspense } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  Popper,
  MenuList,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Checkbox,
  Autocomplete,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as ValidateIcon,
  Cancel as RejectIcon,
  Comment as CommentIcon,
  Upload as UploadIcon,
  Done as DoneIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  Forward as ForwardIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Groups as GroupsIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  ArrowDropDown as ArrowDropDownIcon,
  AttachFile as AttachFileIcon,
  FolderZip as FolderZipIcon,
  Info as InfoIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { getZoomCompensatedAutocompleteSlotProps } from '../utils/zoomCompensation';
import api from '../services/authService';
import projectsService from '../services/projectsService';
import { DocumentParty, MetadataPrecheckReusePayload, PendingAttachment, Party, User } from '../types';
import { useUpload } from '../contexts/UploadContext';
import { computeUploadFileFingerprint } from '../utils/uploadFingerprint';
import { isOfficeDocumentFilename, openFilePreviewInNewTab, buildCommentedPdfFilename } from '../utils/filePreview';
import { formatPartyLabel, isThirdPartyParty } from '../utils/partyDisplay';
import { FileCategoryChip } from './FileCategoryChip';

const PdfEditorDialog = React.lazy(() => import('./PdfEditorDialog'));
const PdfViewerDialog = React.lazy(() => import('./PdfViewerDialog'));

const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;
const DEFAULT_TRANSMITTAL_CC_EMAIL = 'dcc@nautilusdynamics.com';
const PROJECT_TEAM_CC_VALUE = '__project_team__';
const PROJECT_TEAM_CC_LABEL = 'Project team';

type ProjectTeamMember = { name: string; email: string };

function isSpreadsheetMainFilename(filename?: string): boolean {
  return (filename || '').toLowerCase().endsWith('.xlsx');
}

function uniqueEmails(emails: Array<string | undefined | null>, exclude: Set<string> = new Set()): string[] {
  const seen = new Set(exclude);
  const result: string[] = [];
  for (const raw of emails) {
    const email = (raw || '').trim();
    if (!email) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(email);
  }
  return result;
}

function normalizeCCChip(value: string): string {
  const trimmed = value.trim();
  if (trimmed === PROJECT_TEAM_CC_VALUE || trimmed.toLowerCase() === PROJECT_TEAM_CC_LABEL.toLowerCase()) {
    return PROJECT_TEAM_CC_VALUE;
  }
  return trimmed;
}

function uniqueCCChips(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of values) {
    const value = normalizeCCChip(raw);
    if (!value) continue;
    if (value !== PROJECT_TEAM_CC_VALUE && !value.includes('@')) continue;
    const key = value === PROJECT_TEAM_CC_VALUE ? value : value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(value);
  }
  return result;
}

function expandTransmittalCCRecipients(
  chips: string[],
  teamMembers: ProjectTeamMember[],
  excludeEmails: string[] = [],
): { name: string; email: string }[] {
  const exclude = new Set(excludeEmails.map((email) => email.trim().toLowerCase()).filter(Boolean));
  const expanded: Array<string | undefined> = [];
  const namesByEmail = new Map<string, string>();

  for (const member of teamMembers) {
    const key = member.email.trim().toLowerCase();
    if (key && member.name) {
      namesByEmail.set(key, member.name);
    }
  }

  for (const chip of chips) {
    if (chip === PROJECT_TEAM_CC_VALUE) {
      expanded.push(...teamMembers.map((member) => member.email));
    } else {
      expanded.push(chip);
      if (chip.trim().toLowerCase() === DEFAULT_TRANSMITTAL_CC_EMAIL) {
        namesByEmail.set(DEFAULT_TRANSMITTAL_CC_EMAIL, 'DCC');
      }
    }
  }

  return uniqueEmails(expanded, exclude).map((email) => ({
    name: namesByEmail.get(email.toLowerCase()) || '',
    email,
  }));
}

type ApproverReviewFile = {
  id: number;
  filename: string;
  category: string;
  size: number;
  processing_status?: string;
  has_path?: boolean;
};

const APPROVER_REVIEW_FILE_ORDER: Record<string, number> = {
  main: 0,
  attachment: 1,
  archive: 2,
  native: 3,
  crs: 4,
};

const REVIEW_FILE_ALLOWED_CATEGORIES = new Set(['main', 'attachment', 'archive', 'native', 'crs']);

function toPendingAttachments(files: File[]): PendingAttachment[] {
  return files.map((file) => ({ file, isAppendice: false }));
}

function AttachmentFileList({
  attachments,
  onChange,
  borderColor,
  showAppendixCheckbox = true,
}: {
  attachments: PendingAttachment[];
  onChange: (next: PendingAttachment[]) => void;
  borderColor: string;
  showAppendixCheckbox?: boolean;
}) {
  if (attachments.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {attachments.map((item, index) => (
        <Box
          key={`${item.file.name}-${index}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.75,
            px: 1,
            mb: 0.5,
            border: `1px solid ${borderColor}`,
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {item.file.name}
          </Typography>
          {item.isEditedMainPdf && (
            <Chip
              label="Edited main PDF"
              size="small"
              sx={{
                height: 22,
                fontSize: '0.65rem',
                flexShrink: 0,
              }}
            />
          )}
          {showAppendixCheckbox && !item.isEditedMainPdf && (
            <Tooltip title="Always included in transmittals to the client">
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Checkbox
                  size="small"
                  checked={item.isAppendice}
                  onChange={(e) => {
                    const next = [...attachments];
                    next[index] = { ...item, isAppendice: e.target.checked };
                    onChange(next);
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Appendix
                </Typography>
              </Box>
            </Tooltip>
          )}
          <IconButton
            size="small"
            aria-label={`Remove ${item.file.name}`}
            onClick={() => onChange(attachments.filter((_, i) => i !== index))}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
}

function UploadCategoryHeading({
  label,
  tooltip,
  textSecondary,
}: {
  label: string;
  tooltip: string;
  textSecondary: string;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
      <Typography
        variant="overline"
        sx={{
          letterSpacing: '0.05em',
          fontWeight: 600,
          color: textSecondary,
        }}
      >
        {label}
      </Typography>
      <Tooltip
        arrow
        placement="top"
        slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
        title={tooltip}
      >
        <IconButton
          size="small"
          aria-label={`${label} help`}
          sx={{ color: textSecondary, p: 0.25 }}
        >
          <InfoIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function computeShouldShowAdminTabs(
  activeParties: DocumentParty[] | undefined,
  projectUsers: User[],
  userPartyId: number
): boolean {
  if (!activeParties || activeParties.length === 0) return false;

  return activeParties.some((party) => {
    if (!party?.name) return false;

    if (party.backup_party) {
      if (party.backup_party === userPartyId) return true;

      const backupName = (party as DocumentParty & { backup_party_name?: string }).backup_party_name;
      if (backupName) {
        const backupIsInternal = projectUsers.some((u) => u.name === backupName);
        return !backupIsInternal;
      }
      return false;
    }

    const nameMatch = projectUsers.some((u) => u.name === party.name);
    return !nameMatch;
  });
}

function formatMetadataSkipReason(reason: string | null | undefined): string {
  if (!reason) {
    return 'Metadata precheck was not run.';
  }
  if (reason === 'verify_metadata_manually') {
    return 'Could not read status, ND number, and revision from the first page with enough confidence. Please verify them manually on the stamped preview.';
  }
  if (reason === 'validation_disabled') {
    return 'Metadata precheck is disabled on the server.';
  }
  return `Metadata precheck skipped: ${reason}`;
}

function formatUploadError(err: any): string {
  const status = err?.response?.status;
  const serverError = err?.response?.data?.error;
  if (serverError) {
    return serverError;
  }
  if (status === 502 || status === 504) {
    return 'The server lost connection while processing your PDF (stamping or metadata check). The upload may or may not have completed — refresh the revision list before retrying.';
  }
  if (err?.code === 'ECONNABORTED' || String(err?.message || '').toLowerCase().includes('timeout')) {
    return 'Upload timed out while the server was processing a large PDF. Refresh the revision list before retrying.';
  }
  if (!err?.response) {
    return 'Network error during upload. Check your connection and refresh the revision list before retrying.';
  }
  return 'Failed to upload version';
}

interface RevisionActionsDialogProps {
  open: boolean;
  onClose: () => void;
  onValidate: (comment: string, onBehalfOf?: number) => Promise<void>;
  onReject: (comment: string, file?: File, onBehalfOf?: number, attachments?: PendingAttachment[], archives?: File[], native_files?: File[], crs_files?: File[]) => Promise<void>;
  onUpload: (files: { main?: File, attachments: PendingAttachment[], archives: File[], native_files: File[], crs_files: File[], tempFileId?: string, metadataPrecheckReuse?: MetadataPrecheckReusePayload }, notes: string, onBehalfOf?: number) => Promise<void>;
  onForward: (backupPartyId: number, originalPartyId?: number) => Promise<void>;
  onCancelRevision?: () => Promise<void>;
  onTransmittal?: (mainRecipients: { name: string; email: string }[], otherRecipients: { name: string; email: string }[], fileIds: number[], message: string, expiresInDays: number) => Promise<void>;
  userRole: 'author' | 'checker' | 'approver' | null;
  canValidate: boolean;
  canReject: boolean;
  canUpload: boolean;
  canForward: boolean;
  canCancelRevision?: boolean;
  canSendTransmittal?: boolean;
  isDocumentController?: boolean;
  projectId: number;
  documentId: number;
  revisionId: number;
  revisionStatus?: string;
  downloadParams?: {
    projectId: number;
    documentId: number;
    revisionId: number;
    versionId: number;
    document?: { nd_number?: string; title?: string };
  };
  parties: DocumentParty[];
  projectParties: Party[];
  userPartyId: number;
  projectUsers: User[];
  activeParties?: DocumentParty[];
  isProposalProject?: boolean;
}

const RevisionActionsDialog: React.FC<RevisionActionsDialogProps> = ({
  open,
  onClose,
  onValidate,
  onReject,
  onUpload,
  onForward,
  onCancelRevision,
  onTransmittal,
  userRole,
  canValidate,
  canReject,
  canUpload,
  canForward,
  canCancelRevision = false,
  canSendTransmittal = false,
  isDocumentController = false,
  projectId,
  documentId,
  revisionId,
  revisionStatus,
  downloadParams,
  parties,
  projectParties,
  userPartyId,
  projectUsers,
  activeParties,
  isProposalProject = false,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [comment, setComment] = useState('');
  const [onBehalfOf, setOnBehalfOf] = useState<number | null>(null); // New State

  // File states
  const [mainFile, setMainFile] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [archives, setArchives] = useState<File[]>([]);
  const [rejectAttachments, setRejectAttachments] = useState<PendingAttachment[]>([]);
  const [rejectArchives, setRejectArchives] = useState<File[]>([]);
  const [rejectCrsFiles, setRejectCrsFiles] = useState<File[]>([]);
  const [nativeFiles, setNativeFiles] = useState<File[]>([]);
  const [crsFiles, setCrsFiles] = useState<File[]>([]);

  const [selectedBackupParty, setSelectedBackupParty] = useState<number | null>(null);
  const [selectedOriginalParty, setSelectedOriginalParty] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileDownloaded, setFileDownloaded] = useState(false);
  const [mainPdfReviewed, setMainPdfReviewed] = useState(false);
  const [approverReviewFiles, setApproverReviewFiles] = useState<ApproverReviewFile[]>([]);
  const [approverReviewFilesLoading, setApproverReviewFilesLoading] = useState(false);
  const [approverDownloadedFileIds, setApproverDownloadedFileIds] = useState<Set<number>>(new Set());
  const [downloadingReviewFileId, setDownloadingReviewFileId] = useState<number | null>(null);
  const [checkedConversion, setCheckedConversion] = useState(false);
  const [metadataPrecheckWarnings, setMetadataPrecheckWarnings] = useState<string[]>([]);
  const [metadataPrecheckInfo, setMetadataPrecheckInfo] = useState<string | null>(null);
  const [metadataPrecheckReuse, setMetadataPrecheckReuse] = useState<MetadataPrecheckReusePayload | null>(null);
  const [pdfEditorOpen, setPdfEditorOpen] = useState(false);
  const [pdfEditorSrc, setPdfEditorSrc] = useState<string | null>(null);
  const [pdfEditorFilename, setPdfEditorFilename] = useState('');
  const [pdfEditorLoading, setPdfEditorLoading] = useState(false);
  const [editingReviewFileId, setEditingReviewFileId] = useState<number | null>(null);
  const [conversionPreviewOpen, setConversionPreviewOpen] = useState(false);
  const [conversionPreviewSrc, setConversionPreviewSrc] = useState<string | null>(null);
  const [conversionPreviewFilename, setConversionPreviewFilename] = useState('');
  const [conversionPreviewLoading, setConversionPreviewLoading] = useState(false);

  // Drag states
  const [dragActiveMain, setDragActiveMain] = useState(false);
  const [dragActiveAtt, setDragActiveAtt] = useState(false);
  const [dragActiveArch, setDragActiveArch] = useState(false);
  const [dragActiveNative, setDragActiveNative] = useState(false);
  const [dragActiveCrs, setDragActiveCrs] = useState(false);

  // Preview States managed by Context
  const { startConversion, getConversion, getDraft, setDraft, clearDraft, hasDraft } = useUpload();
  const conversionId = `${projectId}-${documentId}-${revisionId}`;
  const conversionState = getConversion(conversionId);

  const converting = conversionState?.status === 'converting';
  const conversionError = conversionState?.error || null;
  const ALLOWED_MAIN_FILE_EXTENSIONS = isProposalProject
    ? ['pdf', 'doc', 'docx', 'xlsx']
    : ['pdf', 'doc', 'docx'];
  const mainFileAccept = ALLOWED_MAIN_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(',');
  const mainFileTypesLabel = isProposalProject
    ? 'PDF, DOC, DOCX, or XLSX'
    : 'PDF, DOC, or DOCX';
  const ALLOWED_NATIVE_SOURCE_EXTENSIONS = ['doc', 'docx'];
  const FILE_SIZE_LIMIT_BYTES = 50 * 1024 * 1024;
  const mainFileExt = mainFile?.name.split('.').pop()?.toLowerCase() || '';
  const mainRequiresConversion = ['docx', 'doc'].includes(mainFileExt);
  const mainIsPdf = mainFileExt === 'pdf';
  const LARGE_CONVERSION_BYTES = 2 * 1024 * 1024;
  const conversionMayExceedFourMinutes =
    converting
    && !mainIsPdf
    && (
      conversionState?.health === 'slow'
      || (mainFile != null && mainFile.size >= LARGE_CONVERSION_BYTES)
    );
  const mainRequiresPreviewCheck = Boolean(mainFile) && (mainRequiresConversion || mainIsPdf);
  const conversionSatisfied =
    !mainRequiresPreviewCheck ||
    (conversionState?.status === 'done' && !!conversionState?.tempFileId && checkedConversion);
  const uploadDraftActive = hasDraft(conversionId);

  const canFinishUpload = !loading && !converting && conversionSatisfied;

  const shouldShowAdminTabs = computeShouldShowAdminTabs(activeParties, projectUsers, userPartyId);
  const isApprovingPhase = revisionStatus === 'approving_pending';
  const isCheckingPhase = revisionStatus === 'checking_pending';
  const isWorkflowApprover = userRole === 'approver';
  // Approver review UI: workflow approvers, or privileged actors (admin, PM, document_controller)
  // acting during a third-party approver step — same gate as validate/reject tab visibility.
  const canProxyApproverReview = canValidate && shouldShowAdminTabs;
  const showApproverDocumentReview = Boolean(
    downloadParams &&
    isApprovingPhase &&
    (isWorkflowApprover || canProxyApproverReview)
  );
  // Checker review UI: workflow checkers, or privileged actors acting during a
  // third-party checker step. Mirrors the approver gating so the checker can see
  // and download all files the originator uploaded for the latest version.
  const isWorkflowChecker = userRole === 'checker';
  const canProxyCheckerReview = canValidate && shouldShowAdminTabs;
  const showCheckerDocumentReview = Boolean(
    downloadParams &&
    isCheckingPhase &&
    (isWorkflowChecker || canProxyCheckerReview)
  );
  // Shared file list rendered for both checker and approver action dialogs.
  const showReviewFilesList = showApproverDocumentReview || showCheckerDocumentReview;
  const hasSpreadsheetMain = approverReviewFiles.some(
    (file) => (file.category || '').toLowerCase() === 'main' && isSpreadsheetMainFilename(file.filename)
  );
  const mainReviewRequiredMessage = hasSpreadsheetMain
    ? 'You must download the main spreadsheet before validating or rejecting'
    : 'You must review the main PDF before validating or rejecting';

  const closePdfEditor = () => {
    if (pdfEditorSrc) {
      URL.revokeObjectURL(pdfEditorSrc);
    }
    setPdfEditorOpen(false);
    setPdfEditorSrc(null);
    setPdfEditorFilename('');
    setPdfEditorLoading(false);
    setEditingReviewFileId(null);
  };

  useEffect(() => () => {
    if (pdfEditorSrc) {
      URL.revokeObjectURL(pdfEditorSrc);
    }
  }, [pdfEditorSrc]);

  const closeConversionPreview = () => {
    if (conversionPreviewSrc) {
      URL.revokeObjectURL(conversionPreviewSrc);
    }
    setConversionPreviewOpen(false);
    setConversionPreviewSrc(null);
    setConversionPreviewFilename('');
    setConversionPreviewLoading(false);
  };

  useEffect(() => () => {
    if (conversionPreviewSrc) {
      URL.revokeObjectURL(conversionPreviewSrc);
    }
  }, [conversionPreviewSrc]);

  const buildConversionPreviewFilename = () => {
    if (!mainFile?.name) return 'preview.pdf';
    const stem = mainFile.name.replace(/\.[^.]+$/, '');
    return `${stem}.pdf`;
  };

  const handleCheckConversion = async () => {
    if (!conversionState?.tempFileId || conversionPreviewLoading) return;
    setConversionPreviewLoading(true);
    setError(null);
    try {
      let src = conversionPreviewSrc;
      if (!src) {
        const response = await api.get(
          `/projects/convert-preview/materialized/${conversionState.tempFileId}.pdf`,
          { responseType: 'blob' }
        );
        src = URL.createObjectURL(response.data);
        setConversionPreviewSrc(src);
      }
      setConversionPreviewFilename(buildConversionPreviewFilename());
      setConversionPreviewOpen(true);
      setCheckedConversion(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to open stamped preview';
      setError(message);
    } finally {
      setConversionPreviewLoading(false);
    }
  };

  // Clean up preview URL - handled by Context mainly, but if we close we might want to keep it?
  // Context handles revocation on clear. 

  const [existingBackups, setExistingBackups] = useState<Array<{
    role: 'author' | 'checker' | 'approver';
    original_party_id: number;
    original_party_name: string;
    backup_party_id: number;
    backup_party_name: string;
  }>>([]);
  const [backupsLoading, setBackupsLoading] = useState(false);

  // Transmittal states
  const [transmittalMainEmail, setTransmittalMainEmail] = useState('');

  // Transmittal expiration
  const [transmittalExpireOpen, setTransmittalExpireOpen] = useState(false);
  const transmittalExpireAnchorRef = React.useRef<HTMLDivElement>(null);
  const [transmittalExpireSelectedIndex, setTransmittalExpireSelectedIndex] = useState(2); // Index 2 is 5 days
  const expireOptions = [1, 3, 5, 10, 30];

  const handleTransmittalExpireToggle = () => {
    setTransmittalExpireOpen((prevOpen) => !prevOpen);
  };
  const handleTransmittalExpireClose = (event: Event) => {
    if (transmittalExpireAnchorRef.current && transmittalExpireAnchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setTransmittalExpireOpen(false);
  };
  const handleTransmittalExpireSelect = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number) => {
    setTransmittalExpireSelectedIndex(index);
    setTransmittalExpireOpen(false);
  };
  const [transmittalCCRecipients, setTransmittalCCRecipients] = useState<string[]>([]);
  const [transmittalCCInput, setTransmittalCCInput] = useState('');
  const [projectTeamMembers, setProjectTeamMembers] = useState<ProjectTeamMember[]>([]);
  const [transmittalFileIds, setTransmittalFileIds] = useState<number[]>([]);
  const [transmittalMessage, setTransmittalMessage] = useState('Please find enclosed the latest revisions of the following documents for your review and necessary actions.');
  const [transmittalFiles, setTransmittalFiles] = useState<Array<{
    id: number;
    filename: string;
    category: string;
    size: number;
    is_appendice?: boolean;
    mime_type?: string;
    processing_status?: string;
    has_path?: boolean;
  }>>([]);
  const [transmittalLoading, setTransmittalLoading] = useState(false);
  const [viewingTransmittalFileId, setViewingTransmittalFileId] = useState<number | null>(null);
  const [transmittalSuccess, setTransmittalSuccess] = useState(false);

  useEffect(() => {
    if (open && isDocumentController) {
      fetchExistingBackups();
    }
  }, [open, isDocumentController, projectId, documentId, revisionId]);

  useEffect(() => {
    if (!open || !showReviewFilesList || !downloadParams?.versionId) {
      if (!open) {
        setApproverReviewFiles([]);
        setApproverDownloadedFileIds(new Set());
        setDownloadingReviewFileId(null);
      }
      return;
    }

    let cancelled = false;
    setApproverReviewFilesLoading(true);

    projectsService
      .getRevisionVersionFiles(projectId, documentId, revisionId, downloadParams.versionId)
      .then((data) => {
        if (cancelled) return;
        const files = (data.files || [])
          .filter((file: { category?: string }) => REVIEW_FILE_ALLOWED_CATEGORIES.has((file.category || '').toLowerCase()))
          .map((file: { id: number; filename: string; category: string; size?: number; processing_status?: string; has_path?: boolean }) => ({
            id: file.id,
            filename: file.filename,
            category: file.category,
            size: file.size ?? 0,
            processing_status: file.processing_status,
            has_path: file.has_path,
          }))
          .sort(
            (a: ApproverReviewFile, b: ApproverReviewFile) =>
              (APPROVER_REVIEW_FILE_ORDER[a.category] ?? 9) - (APPROVER_REVIEW_FILE_ORDER[b.category] ?? 9)
          );
        setApproverReviewFiles(files);
      })
      .catch(() => {
        if (!cancelled) {
          setApproverReviewFiles([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setApproverReviewFilesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, showReviewFilesList, downloadParams?.versionId, projectId, documentId, revisionId]);

  // Fetch transmittal data when dialog opens and revision is approved
  useEffect(() => {
    if (open && canSendTransmittal && revisionStatus === 'approved') {
      let cancelled = false;

      const usersPromise = projectUsers.length > 0
        ? Promise.resolve(projectUsers)
        : projectsService.getProjectUsers(projectId).catch(() => [] as User[]);

      Promise.all([
        projectsService.getClientContact(projectId).catch(() => ({ poc_emails: [] as string[] })),
        projectsService.getProjectDetails(projectId).catch(() => null),
        usersPromise,
      ]).then(([contact, details, users]) => {
        if (cancelled) return;

        const mainEmails: string[] = [];
        if (contact.poc_emails && Array.isArray(contact.poc_emails)) {
          contact.poc_emails.forEach((email) => {
            if (email && !mainEmails.includes(email)) {
              mainEmails.push(email);
            }
          });
        }
        if (mainEmails.length > 0) {
          setTransmittalMainEmail(mainEmails[0]);
        }

        const userById = new Map(users.map((user) => [user.id, user]));
        const members: ProjectTeamMember[] = [];
        const seenMemberEmails = new Set<string>();
        const addMember = (name?: string, email?: string) => {
          const trimmedEmail = (email || '').trim();
          if (!trimmedEmail) return;
          const key = trimmedEmail.toLowerCase();
          if (seenMemberEmails.has(key)) return;
          seenMemberEmails.add(key);
          members.push({ name: (name || '').trim(), email: trimmedEmail });
        };

        const pmId = details?.project?.pm_id;
        if (typeof pmId === 'number') {
          const pm = userById.get(pmId);
          addMember(pm?.name, pm?.email);
        }
        (details?.project?.engineers || []).forEach((engineer) => {
          const fromUsers = userById.get(engineer.id);
          addMember(engineer.name || fromUsers?.name, engineer.email || fromUsers?.email);
        });

        setProjectTeamMembers(members);
        setTransmittalCCRecipients(uniqueCCChips([
          DEFAULT_TRANSMITTAL_CC_EMAIL,
          ...(members.length > 0 ? [PROJECT_TEAM_CC_VALUE] : []),
        ]));
      });

      // Fetch revision files for selection
      if (downloadParams?.versionId) {
        projectsService.getRevisionVersionFiles(projectId, documentId, revisionId, downloadParams.versionId).then(data => {
          if (cancelled) return;
          const files = (data.files || []).filter((f: { category?: string }) => f.category !== 'native');
          setTransmittalFiles(files);
          const mandatoryIds = files
            .filter((f: { category?: string; is_appendice?: boolean }) =>
              f.category === 'main' || (f.category === 'attachment' && f.is_appendice))
            .map((f: { id: number }) => f.id);
          setTransmittalFileIds(mandatoryIds);
        }).catch(() => { });
      }

      return () => {
        cancelled = true;
      };
    }
  }, [open, canSendTransmittal, revisionStatus, projectId, documentId, revisionId, downloadParams?.versionId, projectUsers]);

  const wasOpenRef = React.useRef(false);
  const prevConversionIdRef = React.useRef(conversionId);
  const restoringDraftRef = React.useRef(false);
  const persistDraftRef = React.useRef(false);

  const draftSnapshotRef = React.useRef({
    mainFile,
    attachments,
    archives,
    nativeFiles,
    crsFiles,
    comment,
    checkedConversion,
    metadataPrecheckReuse,
  });
  draftSnapshotRef.current = {
    mainFile,
    attachments,
    archives,
    nativeFiles,
    crsFiles,
    comment,
    checkedConversion,
    metadataPrecheckReuse,
  };

  const applyDraftToForm = React.useCallback((draftId: string) => {
    const draft = getDraft(draftId);
    restoringDraftRef.current = true;
    setMainFile(draft.mainFile);
    setAttachments(draft.attachments);
    setArchives(draft.archives);
    setNativeFiles(draft.nativeFiles);
    setCrsFiles(draft.crsFiles);
    setComment(draft.comment);
    setCheckedConversion(draft.checkedConversion);
    setMetadataPrecheckReuse(draft.metadataPrecheckReuse);
    restoringDraftRef.current = false;
  }, [getDraft]);

  // Restore persisted upload draft before paint when the dialog opens or the revision changes.
  useLayoutEffect(() => {
    const revisionChanged = prevConversionIdRef.current !== conversionId;

    if (revisionChanged) {
      if (persistDraftRef.current) {
        setDraft(prevConversionIdRef.current, draftSnapshotRef.current);
      }
      prevConversionIdRef.current = conversionId;
      persistDraftRef.current = false;
    }

    if (open) {
      if (revisionChanged || !wasOpenRef.current) {
        applyDraftToForm(conversionId);
      }
      persistDraftRef.current = true;
    }

    wasOpenRef.current = open;
  }, [open, conversionId, setDraft, applyDraftToForm]);

  // Persist upload draft across close/reopen and in-app navigation.
  useEffect(() => {
    if (!persistDraftRef.current || restoringDraftRef.current) return;
    setDraft(conversionId, draftSnapshotRef.current);
  }, [
    conversionId,
    setDraft,
    mainFile,
    attachments,
    archives,
    nativeFiles,
    crsFiles,
    comment,
    checkedConversion,
    metadataPrecheckReuse,
  ]);

  // Flush the latest draft when this dialog unmounts (e.g. navigating away).
  useEffect(() => {
    return () => {
      if (persistDraftRef.current) {
        setDraft(conversionId, draftSnapshotRef.current);
      }
    };
  }, [conversionId, setDraft]);

  // Sync context file to local state if exists and we don't have one
  useEffect(() => {
    if (conversionState?.file && !mainFile) {
      setMainFile(conversionState.file);
    }
  }, [conversionState, mainFile]);

  useEffect(() => {
    if (conversionState?.status !== 'done' || !mainFile) return;
    const warnings = conversionState.validationWarnings || [];
    if (warnings.length > 0) {
      setMetadataPrecheckWarnings(
        warnings.map((w) => `${w.field}: expected ${w.expected}, detected ${w.detected}`)
      );
      setMetadataPrecheckInfo(null);
    } else if (conversionState.metadataValidationExecuted) {
      setMetadataPrecheckWarnings([]);
      setMetadataPrecheckInfo('Metadata precheck passed after conversion.');
    } else if (conversionState.metadataValidationSkipReason) {
      setMetadataPrecheckWarnings([]);
      setMetadataPrecheckInfo(formatMetadataSkipReason(conversionState.metadataValidationSkipReason));
    }

    void (async () => {
      try {
        const fingerprint = await computeUploadFileFingerprint(mainFile);
        setMetadataPrecheckReuse({
          fingerprint,
          executed: !!conversionState.metadataValidationExecuted,
          skipReason: conversionState.metadataValidationSkipReason,
          validationWarnings: warnings,
          extractedFields: conversionState.extractedFields,
        });
      } catch {
        setMetadataPrecheckReuse(null);
      }
    })();
  }, [conversionState, mainFile]);

  const fetchExistingBackups = async () => {
    try {
      setBackupsLoading(true);
      const backups = await projectsService.getRevisionBackupParties(projectId, documentId, revisionId);
      setExistingBackups(backups);
    } catch (err: any) {
      console.error('Failed to fetch provisional users:', err);
    } finally {
      setBackupsLoading(false);
    }
  };

  const handleRemoveBackup = async (originalPartyId: number, role: 'author' | 'checker' | 'approver') => {
    try {
      setLoading(true);
      await projectsService.removeBackupParty(projectId, documentId, revisionId, originalPartyId, role);
      // Refresh backups
      await fetchExistingBackups();
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove provisional user');
    } finally {
      setLoading(false);
    }
  };

  const applyNativeSourceFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_NATIVE_SOURCE_EXTENSIONS.includes(ext)) {
      setError('Native file must be a DOC or DOCX file.');
      return;
    }
    if (file.size > FILE_SIZE_LIMIT_BYTES) {
      alert(`File ${file.name} exceeds the 50MB limit.`);
      return;
    }
    setNativeFiles([file]);
    setError(null);
  };

  const handleDrag = (e: React.DragEvent, type: 'main' | 'att' | 'arch' | 'native' | 'crs') => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      if (type === 'main') setDragActiveMain(true);
      if (type === 'att') setDragActiveAtt(true);
      if (type === 'arch') setDragActiveArch(true);
      if (type === 'native') setDragActiveNative(true);
      if (type === 'crs') setDragActiveCrs(true);
    } else if (e.type === 'dragleave') {
      if (type === 'main') setDragActiveMain(false);
      if (type === 'att') setDragActiveAtt(false);
      if (type === 'arch') setDragActiveArch(false);
      if (type === 'native') setDragActiveNative(false);
      if (type === 'crs') setDragActiveCrs(false);
    }
  };

  const handleDrop = (e: React.DragEvent, type: 'main' | 'att' | 'arch' | 'native' | 'crs') => {
    e.preventDefault();
    e.stopPropagation();
    if (type === 'main') setDragActiveMain(false);
    if (type === 'att') setDragActiveAtt(false);
    if (type === 'arch') setDragActiveArch(false);
    if (type === 'native') setDragActiveNative(false);
    if (type === 'crs') setDragActiveCrs(false);

    if (e.dataTransfer.files) {
      const files = Array.from(e.dataTransfer.files);
      if (type === 'main' && files.length > 0) {
        handleMainFileProcess(files[0]);
      } else if (type === 'att') {
        const pending = toPendingAttachments(files);
        if (availableTabs[activeTab]?.key === 'reject') {
          setRejectAttachments((prev) => [...prev, ...pending]);
        } else {
          setAttachments((prev) => [...prev, ...pending]);
        }
      } else if (type === 'arch') {
        if (availableTabs[activeTab]?.key === 'reject') {
          setRejectArchives((prev) => [...prev, ...files]);
        } else {
          setArchives((prev) => [...prev, ...files]);
        }
      } else if (type === 'native') {
        if (files.length > 0) {
          applyNativeSourceFile(files[0]);
        }
        return;
      } else if (type === 'crs') {
        setCrsFiles(prev => [...prev, ...files]);
      }
      setError(null);
    }
  };

  const handleMainFileProcess = async (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_MAIN_FILE_EXTENSIONS.includes(ext)) {
      setError(`Main document must be a ${mainFileTypesLabel} file.`);
      return;
    }

    if (['docx', 'doc', 'xlsx'].includes(ext)) {
      setNativeFiles([]);
    }

    setMainFile(file);
    setCheckedConversion(false);
    setConversionPreviewOpen(false);
    setConversionPreviewSrc((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return null;
    });
    setConversionPreviewFilename('');
    setConversionPreviewLoading(false);
    setMetadataPrecheckWarnings([]);
    setMetadataPrecheckInfo(null);
    setMetadataPrecheckReuse(null);
    // setConversionError(null); // Context
    // setPreviewUrl(null); // Context

    // Check if file needs preview (DOC/DOCX conversion or PDF stamping)
    if (ext && ['docx', 'doc'].includes(ext)) {
      startConversion(conversionId, file, { projectId, documentId, revisionId });
      setMetadataPrecheckInfo('Running metadata precheck after conversion...');
    } else if (ext === 'pdf') {
      startConversion(conversionId, file, { projectId, documentId, revisionId });
      setMetadataPrecheckInfo('Stamping PDF for preview...');
    }
  };

  const handleDownload = async () => {
    if (downloadParams) {
      try {
        await projectsService.downloadDocumentVersion(
          downloadParams.projectId,
          downloadParams.documentId,
          downloadParams.revisionId,
          downloadParams.versionId,
          downloadParams.document
        );
        setFileDownloaded(true);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to download document');
      }
    }
  };

  const handleReviewMainPdf = async (file: ApproverReviewFile) => {
    if (!downloadParams?.versionId) return;
    if (file.processing_status && file.processing_status !== 'ready') {
      setError('File is still being processed and is not ready for review.');
      return;
    }
    setMainPdfReviewed(true);
    try {
      setEditingReviewFileId(file.id);
      setPdfEditorLoading(true);
      setError(null);
      const blob = await projectsService.getRevisionFileBlob(
        projectId,
        documentId,
        revisionId,
        downloadParams.versionId,
        file.id
      );
      const blobUrl = URL.createObjectURL(blob);
      setPdfEditorSrc(blobUrl);
      setPdfEditorFilename(file.filename);
      setPdfEditorOpen(true);
    } catch (err: any) {
      setError(err.message || err.response?.data?.error || 'Failed to open PDF for review');
    } finally {
      setPdfEditorLoading(false);
      setEditingReviewFileId(null);
    }
  };

  const handleReviewMainSpreadsheet = async (file: ApproverReviewFile) => {
    if (!downloadParams?.versionId) return;
    if (file.processing_status && file.processing_status !== 'ready') {
      setError('File is still being processed and is not ready for review.');
      return;
    }
    try {
      setDownloadingReviewFileId(file.id);
      setError(null);
      await projectsService.downloadRevisionFile(
        projectId,
        documentId,
        revisionId,
        downloadParams.versionId,
        file.id,
        file.filename
      );
      setMainPdfReviewed(true);
    } catch (err: any) {
      setError(err.message || err.response?.data?.error || 'Failed to download spreadsheet for review');
    } finally {
      setDownloadingReviewFileId(null);
    }
  };

  const handleApproverDownloadAuxFile = async (file: ApproverReviewFile) => {
    if (!downloadParams?.versionId) return;
    try {
      setDownloadingReviewFileId(file.id);
      setError(null);
      await projectsService.downloadRevisionFile(
        projectId,
        documentId,
        revisionId,
        downloadParams.versionId,
        file.id,
        file.filename
      );
      setApproverDownloadedFileIds((prev) => new Set(prev).add(file.id));
    } catch (err: any) {
      setError(err.message || err.response?.data?.error || 'Failed to download file');
    } finally {
      setDownloadingReviewFileId(null);
    }
  };

  const handlePdfEditorAddCrs = (file: File): boolean => {
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError('Edited PDF exceeds the 50 MB CRS limit.');
      return false;
    }
    const existingNames = new Set(rejectCrsFiles.map((item) => item.name));
    const nextName = buildCommentedPdfFilename(file.name, existingNames);
    const normalizedFile = nextName === file.name
      ? file
      : new File([file], nextName, { type: file.type || 'application/pdf' });

    setRejectCrsFiles([normalizedFile]);
    return true;
  };

  const handleViewTransmittalFile = async (file: {
    id: number;
    filename: string;
    mime_type?: string;
    processing_status?: string;
    has_path?: boolean;
  }) => {
    if (!downloadParams?.versionId) return;
    if (file.processing_status && file.processing_status !== 'ready') {
      setError('File is still being processed and is not ready for preview.');
      return;
    }
    try {
      setViewingTransmittalFileId(file.id);
      setError(null);
      const blob = await projectsService.getRevisionFileBlob(
        projectId,
        documentId,
        revisionId,
        downloadParams.versionId,
        file.id
      );
      openFilePreviewInNewTab(blob, file.filename, file.mime_type);
    } catch (err: any) {
      setError(err.message || err.response?.data?.error || 'Failed to open file');
    } finally {
      setViewingTransmittalFileId(null);
    }
  };

  const handleValidate = async () => {
    if (showReviewFilesList && !mainPdfReviewed) {
      setError(mainReviewRequiredMessage);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await onValidate(comment, onBehalfOf || undefined);
      handleDismiss();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to validate revision');
    } finally {
      setLoading(false);
    }
  };

  // For Reject, we only allow main file upload as "new version" for simplicity, or we can expand it.
  // The UI currently has single file upload for reject. Let's keep it as Main File for now.
  const handleReject = async () => {
    if (showReviewFilesList && !mainPdfReviewed) {
      setError(mainReviewRequiredMessage);
      return;
    }
    if (!comment.trim()) {
      setError('Comment is required when rejecting');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      // MainFile not used on reject anymore, but pass undefined for the file spot.
      // NOTE: the props have not been updated yet to accept options format for reject so we'll pass undefined for main file later we can update the props to match onUpload
      await onReject(comment, undefined, onBehalfOf || undefined, rejectAttachments, rejectArchives, [], rejectCrsFiles);
      handleCloseAfterSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reject revision');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!mainFile && attachments.length === 0 && archives.length === 0 && nativeFiles.length === 0 && crsFiles.length === 0) {
      setError('At least one file is required for upload');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await onUpload({
        main: mainFile || undefined,
        attachments,
        archives,
        native_files: mainIsPdf ? nativeFiles : [],
        crs_files: crsFiles,
        tempFileId: conversionState?.tempFileId,
        metadataPrecheckReuse: metadataPrecheckReuse || undefined,
      }, comment, onBehalfOf || undefined);
      handleCloseAfterSuccess();
    } catch (err: any) {
      setError(formatUploadError(err));
    } finally {
      setLoading(false);
    }
  };


  const handleForward = async () => {
    if (!selectedBackupParty) {
      setError('Please select a user');
      return;
    }
    if (isDocumentController && !selectedOriginalParty) {
      setError('Please select a provisional user');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await onForward(selectedBackupParty, isDocumentController ? selectedOriginalParty : undefined);
      handleDismiss();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to assign provisional user');
    } finally {
      setLoading(false);
    }
  };


  const resetUploadFormState = () => {
    setComment('');
    setMainFile(null);
    setAttachments([]);
    setArchives([]);
    setRejectAttachments([]);
    setRejectArchives([]);
    setRejectCrsFiles([]);
    setNativeFiles([]);
    setCrsFiles([]);
    setCheckedConversion(false);
    setMetadataPrecheckWarnings([]);
    setMetadataPrecheckInfo(null);
    setMetadataPrecheckReuse(null);
    setDragActiveMain(false);
    setDragActiveAtt(false);
    setDragActiveArch(false);
    setDragActiveNative(false);
    setDragActiveCrs(false);
    setOnBehalfOf(null);
  };

  const resetDialogState = () => {
    resetUploadFormState();
    if (pdfEditorSrc) {
      URL.revokeObjectURL(pdfEditorSrc);
    }
    setPdfEditorOpen(false);
    setPdfEditorSrc(null);
    setPdfEditorFilename('');
    setPdfEditorLoading(false);
    setEditingReviewFileId(null);
    closeConversionPreview();
    setSelectedBackupParty(null);
    setSelectedOriginalParty(null);
    setError(null);
    setActiveTab(0);
    setFileDownloaded(false);
    setMainPdfReviewed(false);
    setApproverReviewFiles([]);
    setApproverDownloadedFileIds(new Set());
    setDownloadingReviewFileId(null);
    setApproverReviewFilesLoading(false);
    setViewingTransmittalFileId(null);
    setTransmittalMessage('Please find enclosed the latest revisions of the following documents for your review and necessary actions.');
  };

  const handleDismiss = () => {
    setDraft(conversionId, {
      mainFile,
      attachments,
      archives,
      nativeFiles,
      crsFiles,
      comment,
      checkedConversion,
      metadataPrecheckReuse,
    });
    setError(null);
    onClose();
  };

  const handleCancelUpload = () => {
    clearDraft(conversionId);
    resetUploadFormState();
    setError(null);
    onClose();
  };

  const handleCloseAfterSuccess = () => {
    clearDraft(conversionId);
    resetDialogState();
    onClose();
  };

  // For document controllers, only show upload/validate/reject tabs if:
  // 1. The user has their own workflow permissions (regular user path), OR
  // 2. At least one active party is a third party (admin "act on behalf" path), OR
  // 3. The user is assigned as a backup party for the current step
  // This ensures the document controller can't act when it's an internal user's turn (who isn't them)
  const isApproved = revisionStatus === 'approved';
  const availableTabs = [];

  if (isApproved) {
    if (canSendTransmittal) {
      availableTabs.push({ key: 'transmittal', icon: <SendIcon />, label: 'Transmittal' });
    }
  } else {
    // If document controller, we need to check if their canValidate/canReject/canUpload 
    // permissions come from being a controller or from their own workflow role
    if (canValidate) {
      // Show validate tab only if user has workflow role OR it's a third party's turn OR they're the backup
      if (!isDocumentController || shouldShowAdminTabs || userRole === 'checker' || userRole === 'approver') {
        availableTabs.push({ key: 'validate', icon: <ValidateIcon />, label: 'Validate' });
      }
    }
    if (canReject) {
      // Show reject tab only if user has workflow role OR it's a third party's turn OR they're the backup
      if (!isDocumentController || shouldShowAdminTabs || userRole === 'checker' || userRole === 'approver') {
        availableTabs.push({ key: 'reject', icon: <RejectIcon />, label: 'Reject' });
      }
    }
    if (canUpload) {
      // Show upload tab only if user has workflow role OR it's a third party's turn OR they're the backup
      if (!isDocumentController || shouldShowAdminTabs || userRole === 'author') {
        availableTabs.push({ key: 'upload', icon: <UploadIcon />, label: 'Upload' });
      }
    }
    if (canForward) availableTabs.push({ key: 'forward', icon: <ForwardIcon />, label: 'Forward' });
    if (canCancelRevision) availableTabs.push({ key: 'cancel-revision', icon: <DeleteIcon />, label: 'Cancel Revision' });
  }

  // Filter parties for "Sign As" dropdown
  const signableParties = isDocumentController
    ? parties.filter(p => p.role === 'author' || (p.status === 'pending' && (p.role === 'checker' || p.role === 'approver')))
    : [];

  const getTargetPartyForTab = (tabKey?: string) => {
    if (tabKey === 'upload') return signableParties.find(p => p.role === 'author');
    if (tabKey === 'validate' || tabKey === 'reject') return signableParties.find(p => p.status === 'pending');
    return undefined;
  };

  const isThirdParty = (party?: Partial<Party>) => isThirdPartyParty(party ?? {}, projectUsers);

  const currentTabKey = availableTabs[activeTab]?.key;
  const targetParty = getTargetPartyForTab(currentTabKey);
  const isValidateOrRejectTab = currentTabKey === 'validate' || currentTabKey === 'reject';
  const showSharedDocumentReview = showReviewFilesList && isValidateOrRejectTab;
  const disableValidateOrRejectFields = showSharedDocumentReview && !mainPdfReviewed;
  const rejectTabIndex = availableTabs.findIndex((tab) => tab.key === 'reject');

  const handlePdfEditorRejectWithComments = (file: File) => {
    if (!handlePdfEditorAddCrs(file)) return;
    if (rejectTabIndex >= 0) {
      setActiveTab(rejectTabIndex);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const nextTabKey = availableTabs[newValue]?.key;
    const previousTabKey = availableTabs[activeTab]?.key;
    const switchingBetweenValidateAndReject =
      (previousTabKey === 'validate' || previousTabKey === 'reject') &&
      (nextTabKey === 'validate' || nextTabKey === 'reject');

    setActiveTab(newValue);
    setError(null);
    setComment('');
    setOnBehalfOf(null);
    closePdfEditor();

    if (switchingBetweenValidateAndReject) {
      return;
    }

    setMainFile(null);
    setAttachments([]);
    setArchives([]);
    setRejectAttachments([]);
    setRejectArchives([]);
    setRejectCrsFiles([]);
    setNativeFiles([]);
    setCrsFiles([]);
    setSelectedBackupParty(null);
    setSelectedOriginalParty(null);
    setDragActiveMain(false);
    setDragActiveAtt(false);
    setDragActiveArch(false);
    setDragActiveNative(false);
    setDragActiveCrs(false);
    setFileDownloaded(false);
    setMainPdfReviewed(false);
    setApproverDownloadedFileIds(new Set());
    setTransmittalMessage('Please find enclosed the latest revisions of the following documents for your review and necessary actions.');
    setTransmittalMainEmail('');
    setTransmittalSuccess(false);
  };

  // Use activeParties from props if available (source of truth for "whose turn is it"), 
  // otherwise fallback to targetParty logic
  const partiesToConsider = activeParties && activeParties.length > 0
    ? activeParties
    : (targetParty ? [targetParty] : []);

  // First gate: if the current workflow actor is the logged-in user,
  // admin "act as" controls must stay hidden.
  const isCurrentStepAssignedToCurrentUser = partiesToConsider.some((party) => {
    const effectiveActorPartyId = party.backup_party ?? party.party_id;
    return effectiveActorPartyId === userPartyId;
  });

  // Show "Sign As" only when:
  // 1) the current workflow step is NOT assigned to the logged-in user, and
  // 2) at least one third party is assigned to the project.
  const showSignAs =
    isDocumentController &&
    !isCurrentStepAssignedToCurrentUser &&
    projectParties.some(p => isThirdParty(p));

  // "On behalf of" options should come from project third parties.
  // This matches the visibility condition and allows explicit selection
  // whenever the project has third-party assignments.
  const signAsOptions = isDocumentController
    ? projectParties
      .filter(p => isThirdParty(p))
      .map(p => ({
        party_id: p.id,
        name: p.name,
        role: p.role_default || 'party',
        contact: p.contact,
      }))
    : [];

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Nautilus Console Design System colors
  const consoleColors = {
    background: isDark ? '#0a1622' : '#f5f5f5',
    surface: isDark ? '#0f2134' : '#ffffff',
    border: isDark ? 'rgba(204, 186, 155, 0.15)' : 'rgba(15, 33, 52, 0.12)',
    borderStrong: isDark ? '#2c3e50' : '#e0e0e0',
    accent: '#ccba9b',
    textPrimary: isDark ? '#ffffff' : '#0f2134',
    textSecondary: isDark ? '#b0bec5' : '#546e7a',
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleDismiss}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: consoleColors.surface,
          border: `1px solid ${consoleColors.borderStrong}`,
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${consoleColors.border}`,
          bgcolor: isDark ? alpha('#0f2134', 0.5) : alpha('#0f2134', 0.03),
          py: 2,
          px: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: consoleColors.textPrimary,
          }}
        >
          Revision Actions
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: consoleColors.surface, p: 3 }}>
        {showSharedDocumentReview && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: isDark ? alpha('#0f2134', 0.6) : alpha('#0f2134', 0.02),
              borderRadius: 1,
              border: `1px solid ${consoleColors.border}`,
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: consoleColors.textSecondary,
                mb: 1,
                display: 'block',
                letterSpacing: '0.05em',
                fontWeight: 500,
              }}
            >
              Document Review
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {hasSpreadsheetMain
                ? 'Download the main spreadsheet before validating or rejecting. Download other files uploaded by the originator as needed.'
                : 'Review the main PDF before validating or rejecting. Download other files uploaded by the originator as needed.'}
            </Typography>
            {approverReviewFilesLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <CircularProgress size={18} sx={{ color: consoleColors.accent }} />
                <Typography variant="body2" color="text.secondary">
                  Loading revision files...
                </Typography>
              </Box>
            ) : approverReviewFiles.length > 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  borderColor: consoleColors.borderStrong,
                  bgcolor: isDark ? alpha('#0a1622', 0.35) : alpha('#0f2134', 0.02),
                }}
              >
                <List dense disablePadding>
                  {approverReviewFiles.map((file) => {
                    const category = (file.category || '').toLowerCase();
                    const isMain = category === 'main';
                    const isSpreadsheetMain = isMain && isSpreadsheetMainFilename(file.filename);
                    const isReady =
                      (file.processing_status || 'ready') === 'ready' && file.has_path !== false;
                    const isReviewed = isMain ? mainPdfReviewed : approverDownloadedFileIds.has(file.id);
                    const isBusy = isSpreadsheetMain || !isMain
                      ? downloadingReviewFileId === file.id
                      : false;
                    const categoryLabel =
                      category === 'main'
                        ? (isSpreadsheetMain ? 'Main spreadsheet' : 'Main PDF')
                        : category === 'attachment'
                          ? 'Attachment'
                          : category === 'archive'
                            ? 'Internal Archive'
                            : category === 'native'
                              ? 'Native File'
                              : 'CRS';
                    const categoryIcon =
                      category === 'main' ? (
                        <DescriptionIcon fontSize="small" />
                      ) : category === 'attachment' ? (
                        <AttachFileIcon fontSize="small" />
                      ) : category === 'archive' ? (
                        <FolderZipIcon fontSize="small" />
                      ) : category === 'native' ? (
                        <DescriptionIcon fontSize="small" />
                      ) : (
                        <CommentIcon fontSize="small" />
                      );

                    const showMainPdfReview = isMain && !isSpreadsheetMain;
                    const reviewActionButtonSx = {
                      py: 0.35,
                      px: 1,
                      minWidth: 0,
                      fontSize: '0.75rem',
                      lineHeight: 1.3,
                      '& .MuiButton-startIcon': { mr: 0.5 },
                    };
                    const isMainReviewBusy = showMainPdfReview && editingReviewFileId === file.id;

                    return (
                      <ListItem
                        key={file.id}
                        sx={{
                          px: 2,
                          py: 1.25,
                          borderBottom: `1px solid ${consoleColors.border}`,
                          '&:last-child': { borderBottom: 'none' },
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32, mt: 0.25, color: consoleColors.textSecondary }}>
                          {categoryIcon}
                        </ListItemIcon>
                        <ListItemText
                          sx={{ flex: 1, minWidth: 0, m: 0 }}
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <FileCategoryChip
                                category={category}
                                label={categoryLabel}
                                sx={{
                                  borderColor: consoleColors.borderStrong,
                                  color: consoleColors.textSecondary,
                                }}
                              />
                              {!isReady && (
                                <Typography variant="caption" color="warning.main">
                                  Processing
                                </Typography>
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                color: consoleColors.textPrimary,
                                wordBreak: 'break-all',
                              }}
                            >
                              {file.filename}
                              {file.size > 0 && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{ display: 'block', mt: 0.25, color: consoleColors.textSecondary }}
                                >
                                  {(file.size / 1024).toFixed(0)} KB
                                </Typography>
                              )}
                            </Typography>
                          }
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 0.75,
                            flexShrink: 0,
                            alignItems: 'stretch',
                            alignSelf: 'flex-start',
                            width: 'max-content',
                          }}
                        >
                          {showMainPdfReview ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                isMainReviewBusy ? (
                                  <CircularProgress size={14} sx={{ color: consoleColors.accent }} />
                                ) : isReviewed ? (
                                  <DoneIcon sx={{ fontSize: 16 }} />
                                ) : (
                                  <ViewIcon sx={{ fontSize: 16 }} />
                                )
                              }
                              onClick={() => void handleReviewMainPdf(file)}
                              disabled={!isReady || pdfEditorLoading}
                              sx={{
                                ...reviewActionButtonSx,
                                borderColor: isReviewed ? consoleColors.accent : consoleColors.borderStrong,
                                color: isReviewed ? consoleColors.accent : consoleColors.textPrimary,
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                  borderColor: consoleColors.accent,
                                  bgcolor: alpha(consoleColors.accent, 0.08),
                                },
                              }}
                            >
                              {isReviewed ? 'Reviewed' : 'Review'}
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                isBusy ? (
                                  <CircularProgress size={14} sx={{ color: consoleColors.accent }} />
                                ) : isReviewed ? (
                                  <DoneIcon sx={{ fontSize: 16 }} />
                                ) : (
                                  <DownloadIcon sx={{ fontSize: 16 }} />
                                )
                              }
                              onClick={() => {
                                if (!isReady || isBusy) return;
                                if (isSpreadsheetMain) {
                                  void handleReviewMainSpreadsheet(file);
                                  return;
                                }
                                if (isReviewed) return;
                                void handleApproverDownloadAuxFile(file);
                              }}
                              disabled={!isReady || isBusy || (!isSpreadsheetMain && isReviewed)}
                              sx={{
                                ...reviewActionButtonSx,
                                borderColor: isReviewed ? consoleColors.accent : consoleColors.borderStrong,
                                color: isReviewed ? consoleColors.accent : consoleColors.textPrimary,
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                  borderColor: consoleColors.accent,
                                  bgcolor: alpha(consoleColors.accent, 0.08),
                                },
                              }}
                            >
                              {isReviewed ? 'Downloaded' : 'Download'}
                            </Button>
                          )}
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No review files available for this revision.
              </Typography>
            )}
          </Box>
        )}

        {availableTabs.length > 1 && (
          <Box
            sx={{
              borderBottom: `1px solid ${consoleColors.border}`,
              mb: 3,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  color: consoleColors.textSecondary,
                  '&.Mui-selected': {
                    color: consoleColors.accent,
                  },
                  '&.Mui-disabled': {
                    opacity: 0.45,
                  },
                },
                '& .MuiTabs-indicator': {
                  bgcolor: consoleColors.accent,
                  height: 3,
                },
              }}
            >
              {availableTabs.map((tab) => (
                <Tab
                  key={tab.key}
                  icon={tab.icon}
                  label={tab.label}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>
        )}

        {downloadParams && !showReviewFilesList && !isApproved && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: isDark ? alpha('#0f2134', 0.6) : alpha('#0f2134', 0.02),
              borderRadius: 1,
              border: `1px solid ${consoleColors.border}`,
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: consoleColors.textSecondary,
                mb: 1,
                display: 'block',
                letterSpacing: '0.05em',
                fontWeight: 500,
              }}
            >
              Document Review
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Download the document for review
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={fileDownloaded}
              sx={{
                borderColor: fileDownloaded ? consoleColors.accent : consoleColors.borderStrong,
                color: fileDownloaded ? consoleColors.accent : consoleColors.textPrimary,
                '&:hover': {
                  borderColor: consoleColors.accent,
                  bgcolor: alpha(consoleColors.accent, 0.08),
                },
              }}
            >
              {fileDownloaded ? 'Document Downloaded' : 'Download Document'}
            </Button>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Validate Tab */}
        {availableTabs[activeTab]?.key === 'validate' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              As a {userRole}, you can validate this revision. Add an optional comment below.
            </Typography>
            <Box
              sx={{
                opacity: disableValidateOrRejectFields ? 0.55 : 1,
                pointerEvents: disableValidateOrRejectFields ? 'none' : 'auto',
                transition: 'opacity 0.2s ease',
              }}
            >
              {showSignAs && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Admin: Sign on behalf of another party (Optional)
                  </Typography>
                  <Autocomplete
                    options={signAsOptions}
                    getOptionLabel={(option) => `${option.name} (${option.role})`}
                    value={signAsOptions.find(p => p.party_id === onBehalfOf) || null}
                    onChange={(_, newValue) => setOnBehalfOf(newValue?.party_id || null)}
                    renderInput={(params) => <TextField {...params} label="Sign As..." />}
                    slotProps={getZoomCompensatedAutocompleteSlotProps()}
                    isOptionEqualToValue={(option, value) => option.party_id === value.party_id}
                  />
                </Box>
              )}

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Comment (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add any comments about your validation..."
                disabled={disableValidateOrRejectFields}
              />
            </Box>
          </Box>
        )}

        {/* Reject Tab */}
        {availableTabs[activeTab]?.key === 'reject' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Rejecting this revision will reset the workflow. Please provide a reason. You can optionally attach supporting files, or review the main PDF above and attach a commented copy with your rejection.
            </Typography>
            <Box
              sx={{
                opacity: disableValidateOrRejectFields ? 0.55 : 1,
                pointerEvents: disableValidateOrRejectFields ? 'none' : 'auto',
                transition: 'opacity 0.2s ease',
              }}
            >
              {showSignAs && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Admin: Reject on behalf of another party (Optional)
                  </Typography>
                  <Autocomplete
                    options={signAsOptions}
                    getOptionLabel={(option) => `${option.name} (${option.role})`}
                    value={signAsOptions.find(p => p.party_id === onBehalfOf) || null}
                    onChange={(_, newValue) => setOnBehalfOf(newValue?.party_id || null)}
                    renderInput={(params) => <TextField {...params} label="Reject As..." />}
                    slotProps={getZoomCompensatedAutocompleteSlotProps()}
                    isOptionEqualToValue={(option, value) => option.party_id === value.party_id}
                  />
                </Box>
              )}
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Rejection Reason"
                value={comment}
                error={Boolean(error) && !comment.trim()}
                onChange={(e) => {
                  if (error) setError(null);
                  setComment(e.target.value);
                }}
                placeholder="Explain why you are rejecting this revision..."
                required
                disabled={disableValidateOrRejectFields}
                sx={{ mb: 3 }}
              />

              {rejectCrsFiles.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      mb: 1,
                      display: 'block',
                      letterSpacing: '0.05em',
                      fontWeight: 600,
                      color: consoleColors.textSecondary,
                    }}
                  >
                    CRS (Comment Response Sheet)
                  </Typography>
                  {rejectCrsFiles.map((file, index) => (
                    <Box
                      key={`${file.name}-${index}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.75,
                        px: 1,
                        mb: 0.5,
                        border: `1px solid ${consoleColors.border}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {file.name}
                      </Typography>
                      <FileCategoryChip category="crs" sx={{ flexShrink: 0 }} />
                      <IconButton
                        size="small"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => setRejectCrsFiles((prev) => prev.filter((_, i) => i !== index))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Attachments Section */}
              <Typography
                variant="overline"
                sx={{
                  mb: 1,
                  display: 'block',
                  letterSpacing: '0.05em',
                  fontWeight: 600,
                  color: consoleColors.textSecondary,
                }}
              >
                Attachments
              </Typography>
              <Paper
                sx={{
                  p: 3, mb: 2,
                  border: '2px dashed',
                  borderColor: dragActiveAtt ? consoleColors.accent : consoleColors.borderStrong,
                  backgroundColor: dragActiveAtt
                    ? alpha(consoleColors.accent, 0.08)
                    : (isDark ? alpha('#0f2134', 0.4) : alpha('#0f2134', 0.02)),
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: consoleColors.accent,
                    bgcolor: alpha(consoleColors.accent, 0.04),
                  },
                }}
                onDragEnter={(e) => handleDrag(e, 'att')}
                onDragLeave={(e) => handleDrag(e, 'att')}
                onDragOver={(e) => handleDrag(e, 'att')}
                onDrop={(e) => handleDrop(e, 'att')}
              >
                <input
                  accept="*/*" style={{ display: 'none' }} id="upload-attachments-reject" type="file" multiple
                  onChange={(e) => {
                    if (e.target.files?.length) {
                      Array.from(e.target.files).forEach(file => {
                        if (file.size > MAX_ATTACHMENT_BYTES) {
                          alert(`File ${file.name} exceeds the 50MB limit.`);
                        } else {
                          setRejectAttachments(prev => [...prev, { file, isAppendice: false }]);
                        }
                      });
                    }
                  }}
                />
                <label htmlFor="upload-attachments-reject">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CloudUploadIcon
                      sx={{
                        fontSize: 32,
                        mb: 1,
                        color: rejectAttachments.length > 0 ? consoleColors.accent : consoleColors.textSecondary,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Drag & Drop or Click to Add Attachments
                    </Typography>
                  </Box>
                </label>
              </Paper>
              {rejectAttachments.length > 0 && (
                <AttachmentFileList
                  attachments={rejectAttachments}
                  onChange={setRejectAttachments}
                  borderColor={consoleColors.border}
                  showAppendixCheckbox={false}
                />
              )}

              {/* Internal Archives Section */}
              <Typography
                variant="overline"
                sx={{
                  mb: 1,
                  display: 'block',
                  letterSpacing: '0.05em',
                  fontWeight: 600,
                  color: consoleColors.textSecondary,
                }}
              >
                Internal Archives (Not shared with client)
              </Typography>
              <Paper
                sx={{
                  p: 3, mb: 2,
                  border: '2px dashed',
                  borderColor: dragActiveArch ? consoleColors.accent : consoleColors.borderStrong,
                  backgroundColor: dragActiveArch
                    ? alpha(consoleColors.accent, 0.08)
                    : (isDark ? alpha('#0f2134', 0.4) : alpha('#0f2134', 0.02)),
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: consoleColors.accent,
                    bgcolor: alpha(consoleColors.accent, 0.04),
                  },
                }}
                onDragEnter={(e) => handleDrag(e, 'arch')}
                onDragLeave={(e) => handleDrag(e, 'arch')}
                onDragOver={(e) => handleDrag(e, 'arch')}
                onDrop={(e) => handleDrop(e, 'arch')}
              >
                <input
                  accept="*/*" style={{ display: 'none' }} id="upload-archives-reject" type="file" multiple
                  onChange={(e) => {
                    if (e.target.files?.length) {
                      Array.from(e.target.files).forEach(file => {
                        if (file.size > MAX_ATTACHMENT_BYTES) {
                          alert(`File ${file.name} exceeds the 50MB limit.`);
                        } else {
                          setRejectArchives(prev => [...prev, file]);
                        }
                      });
                    }
                  }}
                />
                <label htmlFor="upload-archives-reject">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CloudUploadIcon
                      sx={{
                        fontSize: 32,
                        mb: 1,
                        color: rejectArchives.length > 0 ? consoleColors.accent : consoleColors.textSecondary,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Drag & Drop or Click to Add Internal Archives
                    </Typography>
                  </Box>
                </label>
              </Paper>
              {rejectArchives.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {rejectArchives.map((file, index) => (
                  <Chip
                    key={`arc-${index}`}
                    label={file.name}
                    onDelete={() => {
                      const newArch = [...rejectArchives];
                      newArch.splice(index, 1);
                      setRejectArchives(newArch);
                    }}
                    deleteIcon={<DeleteIcon />}
                    size="small"
                    sx={{
                      bgcolor: alpha(consoleColors.accent, 0.1),
                      border: `1px solid ${alpha(consoleColors.accent, 0.2)}`,
                    }}
                  />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Upload Tab */}
        {availableTabs[activeTab]?.key === 'upload' && (
          <Box>
            {showSignAs && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Admin: Upload on behalf of another party (Optional)
                </Typography>
                <Autocomplete
                  options={signAsOptions}
                  getOptionLabel={(option) => `${option.name} (${option.role})`}
                  value={signAsOptions.find(p => p.party_id === onBehalfOf) || null}
                  onChange={(_, newValue) => setOnBehalfOf(newValue?.party_id || null)}
                  renderInput={(params) => <TextField {...params} label="Upload As..." />}
                  slotProps={getZoomCompensatedAutocompleteSlotProps()}
                  isOptionEqualToValue={(option, value) => option.party_id === value.party_id}
                />
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a new version for this revision.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add notes about this version..."
              sx={{ mb: 3 }}
            />

            {/* Main Document Upload */}
            <UploadCategoryHeading
              label="Main Document"
              textSecondary={consoleColors.textSecondary}
              tooltip={
                isProposalProject
                  ? 'The official file for this revision version. Upload a PDF, a DOC/DOCX file that will be converted to PDF, or an XLSX spreadsheet stored as-is.'
                  : 'The official stamped PDF for this revision version. Upload a PDF, or a DOC/DOCX file that will be converted to PDF.'
              }
            />
            <Paper
              sx={{
                p: 3, mb: 2,
                border: '2px dashed',
                borderColor: dragActiveMain ? consoleColors.accent : consoleColors.borderStrong,
                backgroundColor: dragActiveMain
                  ? alpha(consoleColors.accent, 0.08)
                  : (isDark ? alpha('#0f2134', 0.4) : alpha('#0f2134', 0.02)),
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: consoleColors.accent,
                  bgcolor: alpha(consoleColors.accent, 0.04),
                },
              }}
              onDragEnter={(e) => handleDrag(e, 'main')}
              onDragLeave={(e) => handleDrag(e, 'main')}
              onDragOver={(e) => handleDrag(e, 'main')}
              onDrop={(e) => handleDrop(e, 'main')}
            >
              <input
                accept={mainFileAccept} style={{ display: 'none' }} id="upload-main" type="file"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    if (e.target.files[0].size > 50 * 1024 * 1024) {
                      alert(`File ${e.target.files[0].name} exceeds the 50MB limit.`);
                    } else {
                      handleMainFileProcess(e.target.files[0]);
                    }
                  }
                }}
              />
              <label htmlFor="upload-main">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 48,
                      mb: 1.5,
                      color: mainFile ? consoleColors.accent : consoleColors.textSecondary,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: mainFile ? consoleColors.textPrimary : consoleColors.textSecondary }}
                  >
                    {mainFile
                      ? mainFile.name
                      : `Drag & Drop or Click to Upload Main Document (${ALLOWED_MAIN_FILE_EXTENSIONS.map((ext) => ext.toUpperCase()).join(', ')})`}
                  </Typography>
                </Box>
              </label>
            </Paper>

            {converting && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                mb={2}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: alpha(consoleColors.accent, 0.08),
                  border: `1px solid ${alpha(consoleColors.accent, 0.2)}`,
                }}
              >
                <CircularProgress size={24} sx={{ mb: 1, color: consoleColors.accent }} />
                {conversionState?.workerName && (
                  <Typography
                    variant="caption"
                    sx={{ mb: 0.5, color: consoleColors.accent, letterSpacing: '0.04em', textAlign: 'center' }}
                  >
                    {conversionState.workerName}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: consoleColors.textPrimary, textAlign: 'center' }}>
                  {conversionState?.progressMessage
                    || (mainIsPdf ? 'Stamping the PDF…' : 'Converting the document…')}
                </Typography>
                {conversionState?.health === 'stalled' ? (
                  <Alert severity="error" sx={{ mt: 1.5, width: '100%' }}>
                    {conversionState.healthMessage || 'The converter stopped reporting.'}
                  </Alert>
                ) : conversionMayExceedFourMinutes ? (
                  <Alert severity="warning" sx={{ mt: 1.5, width: '100%' }}>
                    {conversionState?.health === 'slow' && conversionState.healthMessage
                      ? conversionState.healthMessage
                      : 'This file is large, so conversion may take more than 4 minutes. It will keep running for up to 15 minutes.'}
                  </Alert>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.5,
                      textAlign: 'center',
                      color: consoleColors.textSecondary,
                    }}
                  >
                    {conversionState?.healthMessage || 'Running normally.'}
                  </Typography>
                )}
              </Box>
            )}

            {conversionError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {conversionError}
                {mainRequiresPreviewCheck && (
                  <> You must fix the preview or upload a different file before finishing this task.</>
                )}
              </Alert>
            )}

            {mainRequiresPreviewCheck && !converting && !conversionError && conversionState?.status !== 'done' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {mainRequiresConversion
                  ? 'This file must be converted to PDF before you can finish. Wait for conversion to complete, then check the stamped preview.'
                  : 'This PDF must be stamped before you can finish. Wait for stamping to complete, then check the stamped preview.'}
              </Alert>
            )}

            {metadataPrecheckWarnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Metadata precheck warnings: {metadataPrecheckWarnings.join(' | ')}
              </Alert>
            )}

            {metadataPrecheckInfo && metadataPrecheckWarnings.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {metadataPrecheckInfo}
              </Alert>
            )}

            {conversionState?.status === 'done' && conversionState?.tempFileId && !checkedConversion && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="info">
                  {mainRequiresConversion
                    ? <>Conversion is ready. Click <b>Check Preview</b> to review the stamped PDF.</>
                    : <>Stamping is ready. Click <b>Check Preview</b> to review the stamped PDF.</>}
                </Alert>
              </Box>
            )}

            {mainIsPdf && (
              <>
                <UploadCategoryHeading
                  label="Native File"
                  textSecondary={consoleColors.textSecondary}
                  tooltip="Optional Word source kept as the native original when the main document is a PDF. Upload one DOC or DOCX file."
                />
                <Paper
                  sx={{
                    p: 2, mb: 2,
                    border: '2px dashed',
                    borderColor: dragActiveNative ? consoleColors.accent : consoleColors.borderStrong,
                    backgroundColor: dragActiveNative
                      ? alpha(consoleColors.accent, 0.08)
                      : (isDark ? alpha('#0f2134', 0.4) : alpha('#0f2134', 0.02)),
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: consoleColors.accent,
                      bgcolor: alpha(consoleColors.accent, 0.04),
                    },
                  }}
                  onDragEnter={(e) => handleDrag(e, 'native')}
                  onDragLeave={(e) => handleDrag(e, 'native')}
                  onDragOver={(e) => handleDrag(e, 'native')}
                  onDrop={(e) => handleDrop(e, 'native')}
                >
                  <input
                    accept=".doc,.docx"
                    style={{ display: 'none' }}
                    id="upload-native"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        applyNativeSourceFile(file);
                      }
                      e.target.value = '';
                    }}
                  />
                  <label htmlFor="upload-native">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <CloudUploadIcon
                        sx={{
                          fontSize: 40,
                          mb: 1,
                          color: nativeFiles.length > 0 ? consoleColors.accent : consoleColors.textSecondary,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: nativeFiles.length > 0 ? consoleColors.textPrimary : consoleColors.textSecondary }}
                      >
                        {nativeFiles[0]
                          ? nativeFiles[0].name
                          : 'Drag & Drop or Click to Upload Native File (DOC, DOCX)'}
                      </Typography>
                    </Box>
                  </label>
                </Paper>
                {nativeFiles.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {nativeFiles.map((f, i) => (
                      <Chip
                        key={`${f.name}-${i}`}
                        label={f.name}
                        onDelete={() => setNativeFiles([])}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}

            {/* Attachments Upload */}
            <UploadCategoryHeading
              label="Attachments"
              textSecondary={consoleColors.textSecondary}
              tooltip="Supplementary files for this revision. Check Appendix for files that must always be sent to the client via transmittals; unchecked attachments are optional."
            />
            <Paper
              sx={{
                p: 2, mb: 2,
                border: '2px dashed',
                borderColor: dragActiveAtt ? consoleColors.accent : consoleColors.borderStrong,
                backgroundColor: dragActiveAtt
                  ? alpha(consoleColors.accent, 0.08)
                  : (isDark ? alpha('#0f2134', 0.4) : alpha('#0f2134', 0.02)),
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: consoleColors.accent,
                  bgcolor: alpha(consoleColors.accent, 0.04),
                },
              }}
              onDragEnter={(e) => handleDrag(e, 'att')}
              onDragLeave={(e) => handleDrag(e, 'att')}
              onDragOver={(e) => handleDrag(e, 'att')}
              onDrop={(e) => handleDrop(e, 'att')}
            >
              <input
                accept="*/*" style={{ display: 'none' }} id="upload-att" multiple type="file"
                onChange={(e) => {
                  if (e.target.files) {
                    const files = Array.from(e.target.files);
                    const validFiles = files.filter(f => {
                      if (f.size > 50 * 1024 * 1024) {
                        alert(`File ${f.name} exceeds the 50MB limit.`);
                        return false;
                      }
                      return true;
                    });
                    setAttachments(prev => [...prev, ...toPendingAttachments(validFiles)]);
                  }
                }}
              />
              <label htmlFor="upload-att">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 40,
                      mb: 1,
                      color: attachments.length > 0 ? consoleColors.accent : consoleColors.textSecondary,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: consoleColors.textSecondary }}>
                    Drag & Drop or Click to Upload Attachments
                  </Typography>
                </Box>
              </label>
            </Paper>
            {attachments.length > 0 && (
              <AttachmentFileList
                attachments={attachments}
                onChange={setAttachments}
                borderColor={consoleColors.border}
              />
            )}

            {/* Internal Archives Upload */}
            <UploadCategoryHeading
              label="Internal Archives"
              textSecondary={consoleColors.textSecondary}
              tooltip="Internal-only files stored with the revision but not shared with external parties. Example: native DWG source files or an internal checklist."
            />
            <Paper
              sx={{
                p: 2, mb: 2,
                border: '2px dashed',
                borderColor: dragActiveArch ? consoleColors.accent : consoleColors.borderStrong,
                backgroundColor: dragActiveArch
                  ? alpha(consoleColors.accent, 0.08)
                  : (isDark ? alpha('#0f2134', 0.4) : alpha('#0f2134', 0.02)),
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: consoleColors.accent,
                  bgcolor: alpha(consoleColors.accent, 0.04),
                },
              }}
              onDragEnter={(e) => handleDrag(e, 'arch')}
              onDragLeave={(e) => handleDrag(e, 'arch')}
              onDragOver={(e) => handleDrag(e, 'arch')}
              onDrop={(e) => handleDrop(e, 'arch')}
            >
              <input
                accept="*/*" style={{ display: 'none' }} id="upload-arch" multiple type="file"
                onChange={(e) => {
                  if (e.target.files) {
                    const files = Array.from(e.target.files);
                    const validFiles = files.filter(f => {
                      if (f.size > 50 * 1024 * 1024) {
                        alert(`File ${f.name} exceeds the 50MB limit.`);
                        return false;
                      }
                      return true;
                    });
                    setArchives(prev => [...prev, ...validFiles]);
                  }
                }}
              />
              <label htmlFor="upload-arch">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 40,
                      mb: 1,
                      color: archives.length > 0 ? consoleColors.accent : consoleColors.textSecondary,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: consoleColors.textSecondary }}>
                    Drag & Drop or Click to Upload Internal Archives
                  </Typography>
                </Box>
              </label>
            </Paper>
            {archives.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {archives.map((f, i) => (
                  <Chip key={i} label={f.name} onDelete={() => setArchives(prev => prev.filter((_, idx) => idx !== i))} sx={{ mr: 1, mb: 1 }} />
                ))}
              </Box>
            )}



            {/* CRS Upload */}
            <UploadCategoryHeading
              label="Comments Response Sheet (CRS)"
              textSecondary={consoleColors.textSecondary}
              tooltip="Comment response sheets tracking reviewer comments and your replies for this revision. Example: the completed CRS from an IFR review cycle."
            />
            <Paper
              sx={{
                p: 2, mb: 2,
                border: '2px dashed',
                borderColor: dragActiveCrs ? consoleColors.accent : consoleColors.borderStrong,
                backgroundColor: dragActiveCrs
                  ? alpha(consoleColors.accent, 0.08)
                  : (isDark ? alpha('#0f2134', 0.4) : alpha('#0f2134', 0.02)),
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: consoleColors.accent,
                  bgcolor: alpha(consoleColors.accent, 0.04),
                },
              }}
              onDragEnter={(e) => handleDrag(e, 'crs')}
              onDragLeave={(e) => handleDrag(e, 'crs')}
              onDragOver={(e) => handleDrag(e, 'crs')}
              onDrop={(e) => handleDrop(e, 'crs')}
            >
              <input
                accept="*/*" style={{ display: 'none' }} id="upload-crs" multiple type="file"
                onChange={(e) => {
                  if (e.target.files) {
                    const files = Array.from(e.target.files);
                    const validFiles = files.filter(f => {
                      if (f.size > 50 * 1024 * 1024) {
                        alert(`File ${f.name} exceeds the 50MB limit.`);
                        return false;
                      }
                      return true;
                    });
                    setCrsFiles(prev => [...prev, ...validFiles]);
                  }
                }}
              />
              <label htmlFor="upload-crs">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 40,
                      mb: 1,
                      color: crsFiles.length > 0 ? consoleColors.accent : consoleColors.textSecondary,
                    }}
                  />
                  <Typography variant="body2" sx={{ color: consoleColors.textSecondary }}>
                    Drag & Drop or Click to Upload CRS Files
                  </Typography>
                </Box>
              </label>
            </Paper>
            {crsFiles.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {crsFiles.map((f, i) => (
                  <Chip key={i} label={f.name} onDelete={() => setCrsFiles(prev => prev.filter((_, idx) => idx !== i))} sx={{ mr: 1, mb: 1 }} />
                ))}
              </Box>
            )}

          </Box>
        )}

        {/* Forward Tab */}
        {
          availableTabs[activeTab]?.key === 'forward' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isDocumentController
                  ? 'Assign a provisional user for any workflow step. The provisional user will handle the next action once, then the original party resumes.'
                  : `Assign a provisional party for your ${userRole} role. The provisional user will handle the next action once, then you'll resume.`
                }
              </Typography>
              {isDocumentController && (
                <Autocomplete
                  fullWidth
                  sx={{ mb: 2 }}
                  options={parties}
                  getOptionLabel={(party) => `${party.name} (${party.role})`}
                  value={parties.find(p => p.party_id === selectedOriginalParty) || null}
                  onChange={(_, newValue) => setSelectedOriginalParty(newValue?.party_id || null)}
                  slotProps={getZoomCompensatedAutocompleteSlotProps()}
                  renderInput={(params) => <TextField {...params} label="Select User" />}
                />
              )}
              <Autocomplete
                fullWidth
                sx={{ mb: 2 }}
                options={projectParties.filter(party => party.id !== (isDocumentController ? selectedOriginalParty : userPartyId))}
                getOptionLabel={(party) => formatPartyLabel(party, projectUsers)}
                value={projectParties.find(p => p.id === selectedBackupParty) || null}
                onChange={(_, newValue) => setSelectedBackupParty(newValue?.id || null)}
                slotProps={getZoomCompensatedAutocompleteSlotProps()}
                renderInput={(params) => <TextField {...params} label="Select provisional User to Forward" />}
              />
              <Alert severity="info" sx={{ mb: isDocumentController ? 4 : 0 }}>
                The provisional user will be automatically removed after performing one action.
              </Alert>

              {isDocumentController && (
                <Box
                  sx={{
                    pt: 3,
                    borderTop: `1px solid ${consoleColors.border}`,
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      mb: 1,
                      display: 'block',
                      letterSpacing: '0.08em',
                      fontWeight: 600,
                      color: consoleColors.textSecondary,
                    }}
                  >
                    Manage Actions
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    View and manage existing provisional assignments for this revision.
                  </Typography>
                  {backupsLoading ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} sx={{ color: consoleColors.accent }} />
                    </Box>
                  ) : existingBackups.length > 0 ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        borderColor: consoleColors.borderStrong,
                        bgcolor: isDark ? alpha('#0a1622', 0.35) : alpha('#0f2134', 0.02),
                      }}
                    >
                      <List dense disablePadding>
                        {existingBackups.map((backup) => (
                          <ListItem
                            key={`${backup.role}-${backup.original_party_id}-${backup.backup_party_id}`}
                            sx={{
                              px: 2,
                              py: 1.25,
                              borderBottom: `1px solid ${consoleColors.border}`,
                              '&:last-child': { borderBottom: 'none' },
                            }}
                            secondaryAction={
                              <IconButton
                                edge="end"
                                color="error"
                                onClick={() => handleRemoveBackup(backup.original_party_id, backup.role)}
                                disabled={loading}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            }
                          >
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                  <Chip
                                    label={backup.role}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      letterSpacing: '0.05em',
                                      textTransform: 'uppercase',
                                      borderColor: consoleColors.borderStrong,
                                      color: consoleColors.textSecondary,
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ color: consoleColors.textPrimary }}>
                                    {backup.original_party_name} → {backup.backup_party_name}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary">
                                  Provisional assigned — removed automatically after one action
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, fontStyle: 'italic' }}>
                      No provisional users found for this revision.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )
        }

        {/* Cancel Revision Tab */}
        {availableTabs[activeTab]?.key === 'cancel-revision' && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Cancelling will permanently delete this revision and all of its uploaded files.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone. The parent document and other revisions are kept.
            </Typography>
          </Box>
        )}

        {/* Transmittal Tab */}
        {
          availableTabs[activeTab]?.key === 'transmittal' && (
            <Box>
              {transmittalSuccess ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Transmittal sent successfully! Download links have been emailed to the recipients.
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Send approved documents to external recipients via email. Links expire after 5 days by default.
                  </Typography>

                  {/* Main Recipient */}
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ mb: 1, display: 'block', letterSpacing: '0.05em', fontWeight: 500 }}
                  >
                    Main Recipient (Acknowledger)
                  </Typography>
                  <TextField
                    label="Main recipient email address"
                    placeholder="acknowledger@client.com"
                    helperText="One acknowledger only. Add additional people as CC recipients below."
                    size="small"
                    type="email"
                    fullWidth
                    value={transmittalMainEmail}
                    onChange={(e) => setTransmittalMainEmail(e.target.value)}
                    sx={{ mb: 3 }}
                  />

                  {/* CC Recipients */}
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{
                      mb: 1,
                      display: 'block',
                      letterSpacing: '0.05em',
                      fontWeight: 500,
                    }}
                  >
                    CC Recipients
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    filterSelectedOptions
                    options={projectTeamMembers.length > 0 ? [PROJECT_TEAM_CC_VALUE] : []}
                    getOptionLabel={(option) => option === PROJECT_TEAM_CC_VALUE ? PROJECT_TEAM_CC_LABEL : option}
                    value={transmittalCCRecipients}
                    onChange={(_, newValue) => setTransmittalCCRecipients(uniqueCCChips(newValue as string[]))}
                    inputValue={transmittalCCInput}
                    onInputChange={(_, newInputValue) => setTransmittalCCInput(newInputValue)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        if (option === PROJECT_TEAM_CC_VALUE) {
                          return (
                            <Tooltip
                              key={key}
                              arrow
                              placement="top"
                              title={
                                projectTeamMembers.length === 0 ? (
                                  'No project team emails found'
                                ) : (
                                  <Box sx={{ py: 0.25 }}>
                                    {projectTeamMembers.map((member) => (
                                      <Typography
                                        key={member.email}
                                        variant="caption"
                                        display="block"
                                        sx={{ lineHeight: 1.6 }}
                                      >
                                        {member.name ? `${member.name} · ${member.email}` : member.email}
                                      </Typography>
                                    ))}
                                  </Box>
                                )
                              }
                              slotProps={{ tooltip: { sx: { maxWidth: 420 } } }}
                            >
                              <Chip
                                label={PROJECT_TEAM_CC_LABEL}
                                size="small"
                                icon={<GroupsIcon />}
                                color="primary"
                                variant="outlined"
                                {...tagProps}
                              />
                            </Tooltip>
                          );
                        }
                        return (
                          <Chip
                            key={key}
                            label={option}
                            size="small"
                            color="default"
                            variant="outlined"
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="CC Email addresses"
                        placeholder="Type email and press Enter"
                        helperText="Hover Project team to see members. Press Enter after each extra address."
                        size="small"
                      />
                    )}
                    slotProps={getZoomCompensatedAutocompleteSlotProps()}
                    sx={{ mb: 3 }}
                  />

                  {/* File Selection */}
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{
                      mb: 1,
                      display: 'block',
                      letterSpacing: '0.05em',
                      fontWeight: 500,
                    }}
                  >
                    Documents to send
                  </Typography>
                  {transmittalFiles.length > 0 ? (
                    <Paper
                      variant="outlined"
                      sx={{ mb: 3 }}
                    >
                      <List dense disablePadding>
                        {transmittalFiles.map((file) => {
                          const isSelected = transmittalFileIds.includes(file.id);
                          const isMandatoryLocked =
                            file.category === 'main' ||
                            (file.category === 'attachment' && !!file.is_appendice);
                          const isReady =
                            (file.processing_status || 'ready') === 'ready' && file.has_path !== false;
                          const canView = !isOfficeDocumentFilename(file.filename);
                          const isViewing = viewingTransmittalFileId === file.id;
                          return (
                            <ListItem
                              key={file.id}
                              sx={{
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:last-child': { borderBottom: 'none' },
                                cursor: isMandatoryLocked ? 'default' : 'pointer',
                                opacity: isMandatoryLocked ? 0.95 : 1,
                                pr: canView ? '88px !important' : undefined,
                                '&:hover': { bgcolor: isMandatoryLocked ? 'transparent' : 'action.hover' },
                              }}
                              secondaryAction={
                                canView ? (
                                  <Button
                                    size="small"
                                    startIcon={
                                      isViewing ? (
                                        <CircularProgress size={14} />
                                      ) : (
                                        <ViewIcon fontSize="small" />
                                      )
                                    }
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (isReady && !isViewing) {
                                        void handleViewTransmittalFile(file);
                                      }
                                    }}
                                    disabled={!isReady || isViewing}
                                    sx={{
                                      fontSize: '0.7rem',
                                      letterSpacing: '0.05em',
                                      textTransform: 'uppercase',
                                      minWidth: 0,
                                      px: 1,
                                    }}
                                  >
                                    View
                                  </Button>
                                ) : undefined
                              }
                              onClick={() => {
                                if (isMandatoryLocked) return;
                                setTransmittalFileIds(prev =>
                                  isSelected
                                    ? prev.filter(id => id !== file.id)
                                    : [...prev, file.id]
                                );
                              }}
                            >
                              <IconButton
                                size="small"
                                disabled={isMandatoryLocked}
                                sx={{ mr: 1, color: isSelected ? 'primary.main' : 'text.secondary' }}
                              >
                                {isSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                              </IconButton>
                              <ListItemText
                                primary={file.filename}
                                secondary={
                                  <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <FileCategoryChip
                                      category={file.category}
                                      labelStyle="short"
                                    />
                                    {file.category === 'attachment' && !!file.is_appendice && (
                                      <Chip
                                        label="appendix"
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                      />
                                    )}
                                    {file.size > 0 && (
                                      <Typography variant="caption" color="text.secondary">
                                        {(file.size / 1024).toFixed(0)} KB
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Paper>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
                      No files available for this revision.
                    </Typography>
                  )}

                  {/* Cover Note */}
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Cover note (optional)"
                    value={transmittalMessage}
                    onChange={(e) => setTransmittalMessage(e.target.value)}
                    placeholder="Add a message for the recipients..."
                    size="small"
                  />
                </>
              )}
            </Box>
          )
        }

      </DialogContent >
      <DialogActions>
        {availableTabs[activeTab]?.key === 'upload' && uploadDraftActive && (
          <Button onClick={handleCancelUpload} disabled={loading} color="warning">
            Cancel Upload
          </Button>
        )}
        <Button onClick={handleDismiss} disabled={loading}>
          Close
        </Button>
        {availableTabs[activeTab]?.key === 'validate' && (
          <Button
            onClick={handleValidate}
            variant="contained"
            color="success"
            disabled={loading || (showReviewFilesList && !mainPdfReviewed)}
            startIcon={loading ? <CircularProgress size={20} /> : <ValidateIcon />}
          >
            Validate
          </Button>
        )}
        {availableTabs[activeTab]?.key === 'reject' && (
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={loading || (showReviewFilesList && !mainPdfReviewed)}
            startIcon={loading ? <CircularProgress size={20} /> : <RejectIcon />}
          >
            Reject
          </Button>
        )}
        {availableTabs[activeTab]?.key === 'upload' && (
          <>
            {conversionState?.status === 'done' && conversionState?.tempFileId && (
              <Button
                onClick={() => void handleCheckConversion()}
                variant="outlined"
                color="secondary"
                disabled={loading || conversionPreviewLoading || !conversionState?.tempFileId}
                sx={{ mr: 1 }}
                startIcon={
                  conversionPreviewLoading ? (
                    <CircularProgress size={16} />
                  ) : checkedConversion ? (
                    <DoneIcon />
                  ) : (
                    <ViewIcon />
                  )
                }
              >
                {checkedConversion ? 'Preview Checked' : 'Check Preview'}
              </Button>
            )}
            <Button
              onClick={handleUpload}
              variant="contained"
              color="primary"
              disabled={(!mainFile && attachments.length === 0 && archives.length === 0) || !canFinishUpload}
              startIcon={loading ? <CircularProgress size={20} /> : <DoneIcon />}
            >
              {converting ? (mainIsPdf ? 'Stamping...' : 'Converting...') : 'Finish Task'}
            </Button>
          </>
        )}
        {availableTabs[activeTab]?.key === 'forward' && (
          <Button
            onClick={handleForward}
            variant="contained"
            color="primary"
            disabled={loading || !selectedBackupParty || (isDocumentController && !selectedOriginalParty)}
            startIcon={loading ? <CircularProgress size={20} /> : <ForwardIcon />}
          >
            Assign Provisional User
          </Button>
        )}
        {availableTabs[activeTab]?.key === 'cancel-revision' && (
          <Button
            onClick={async () => {
              if (!onCancelRevision) return;
              try {
                setLoading(true);
                setError(null);
                await onCancelRevision();
                handleCloseAfterSuccess();
              } catch (err: any) {
                setError(err.response?.data?.error || 'Failed to cancel revision');
              } finally {
                setLoading(false);
              }
            }}
            variant="contained"
            color="error"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Delete Revision
          </Button>
        )}
        {availableTabs[activeTab]?.key === 'transmittal' && !transmittalSuccess && (
          <React.Fragment>
            <ButtonGroup
              variant="contained"
              ref={transmittalExpireAnchorRef}
              aria-label="split button"
              disabled={loading || !transmittalMainEmail.trim() || transmittalFileIds.length === 0}
            >
              <Button
                onClick={async () => {
                  if (!onTransmittal) return;
                  try {
                    setTransmittalLoading(true);
                    setLoading(true);
                    setError(null);
                    await onTransmittal(
                      [{ name: '', email: transmittalMainEmail.trim() }],
                      expandTransmittalCCRecipients(
                        transmittalCCRecipients,
                        projectTeamMembers,
                        [transmittalMainEmail.trim()],
                      ),
                      transmittalFileIds,
                      transmittalMessage,
                      expireOptions[transmittalExpireSelectedIndex]
                    );
                    setTransmittalSuccess(true);
                  } catch (err: any) {
                    setError(err.response?.data?.error || 'Failed to send transmittal');
                  } finally {
                    setTransmittalLoading(false);
                    setLoading(false);
                  }
                }}
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                color="primary"
              >
                Send Transmittal
              </Button>
              <Button
                size="small"
                aria-controls={transmittalExpireOpen ? 'split-button-menu' : undefined}
                aria-expanded={transmittalExpireOpen ? 'true' : undefined}
                aria-label="select expiration time"
                aria-haspopup="menu"
                onClick={handleTransmittalExpireToggle}
                color="primary"
                sx={{ px: 0, minWidth: '32px' }}
              >
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
            <Popper
              sx={{ zIndex: 1 }}
              open={transmittalExpireOpen}
              anchorEl={transmittalExpireAnchorRef.current}
              role={undefined}
              transition
              disablePortal
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                  }}
                >
                  <Paper>
                    <ClickAwayListener onClickAway={handleTransmittalExpireClose}>
                      <MenuList id="split-button-menu" autoFocusItem>
                        {expireOptions.map((option, index) => (
                          <MenuItem
                            key={option}
                            selected={index === transmittalExpireSelectedIndex}
                            onClick={(event) => handleTransmittalExpireSelect(event, index)}
                          >
                            Expire in {option} day{option !== 1 ? 's' : ''}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </React.Fragment>
        )}
      </DialogActions>
    </Dialog>
    <Suspense fallback={null}>
      <PdfEditorDialog
        open={pdfEditorOpen}
        src={pdfEditorSrc}
        filename={pdfEditorFilename}
        onClose={closePdfEditor}
        onAddAttachment={handlePdfEditorRejectWithComments}
      />
      <PdfViewerDialog
        open={conversionPreviewOpen}
        src={conversionPreviewSrc}
        filename={conversionPreviewFilename}
        title={`Check Preview — ${conversionPreviewFilename}`}
        onClose={closeConversionPreview}
      />
    </Suspense>
  </>
  );
};

export default RevisionActionsDialog;